/**
 * LocalStorage management utilities
 */

const STORAGE_KEY = 'lotto_saved_numbers';

/**
 * Get all saved numbers
 * @returns {Array} Array of saved number sets
 */
export function getSavedNumbers() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return [];
  }
}

/**
 * Save a number set
 * @param {Array<number>} numbers - Array of 6 numbers
 * @param {string} model - Generation model used
 */
export function saveNumbers(numbers, model) {
  try {
    const saved = getSavedNumbers();
    const newEntry = {
      id: Date.now(),
      numbers,
      model,
      savedAt: new Date().toISOString()
    };

    saved.unshift(newEntry); // Add to beginning

    // Keep only latest 50 entries
    if (saved.length > 50) {
      saved.splice(50);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
    return newEntry;
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    throw error;
  }
}

/**
 * Delete a saved number set
 * @param {number} id - ID of the entry to delete
 */
export function deleteNumbers(id) {
  try {
    const saved = getSavedNumbers();
    const filtered = saved.filter(entry => entry.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error deleting from localStorage:', error);
    throw error;
  }
}

/**
 * Clear all saved numbers
 */
export function clearAllNumbers() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    throw error;
  }
}

/**
 * Get number color based on official lotto color coding
 * @param {number} num - Number (1-45)
 * @returns {string} Color code
 */
export function getNumberColor(num) {
  if (num >= 1 && num <= 10) return '#FBC400'; // Yellow
  if (num >= 11 && num <= 20) return '#69C8F2'; // Blue
  if (num >= 21 && num <= 30) return '#FF7272'; // Red
  if (num >= 31 && num <= 40) return '#AAA7A7'; // Gray
  if (num >= 41 && num <= 45) return '#B0D840'; // Green
  return '#000000'; // Black (fallback)
}

/**
 * Format date to short format (YY-MM-DD)
 * @param {string} dateString - ISO date string (e.g., "2026-02-17T00:00:00.000Z")
 * @returns {string} Formatted date (e.g., "26-02-17")
 */
export function formatShortDate(dateString) {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    const year = String(date.getFullYear()).slice(-2); // Last 2 digits
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
}
